
/**
 * Settings → Usage & Limits, as sentences.
 *
 * Kept apart from the component, and free of React, so the wording is testable: every
 * line here is a claim to a business owner about what they may still do today, and a
 * claim the server did not make is worse than no line at all. Three rules follow.
 *
 *   - A limit that is not being applied is never shown as a limit. "12 of 100" for a
 *     quota nothing enforces is a promise nobody keeps; the count is shown alone.
 *   - A safety control is never presented as the customer's plan. When a temporary
 *     restriction or the CRM safety cap is the lower number, the line says so and names
 *     what the plan allows.
 *   - Money is an estimate. AI cost is labelled as one, and never as a charge.
 */

export interface ApprovalUsage {
  crmChangesRequired: number;
  crmChangesRemaining: number;
  crmDailyLimit: number;
  fitsToday: boolean;
  resetsAt: string;
}

/** One line of Settings → Usage & Limits, as the server counted it. */
export interface UsageDimensionView {
  key: 'crm_changes_daily' | 'email_messages_daily' | 'card_captures_daily'
    | 'csv_rows_per_file' | 'csv_rows_monthly' | 'ai_cost_cents_daily' | 'ai_cost_cents_monthly';
  period: 'day' | 'month' | 'file';
  /** Null: no quota on this. Never null for CRM changes. */
  limit: number | null;
  /** What the plan allows, before any temporary safety control. */
  allowance: number | null;
  /** Null for a per-file limit, which has nothing to count across files. */
  used: number | null;
  /** CRM changes whose outcome is not yet known. They hold their place. */
  reserved: number;
  remaining: number | null;
  resetsAt: string | null;
  control: 'allowance' | 'safety';
  source: 'plan' | 'override' | 'restriction' | 'paused' | 'safety_cap' | 'crm_not_enabled';
  enforced: boolean;
}

export interface UsageView {
  generatedAt: string;
  planCode: string;
  paused: boolean;
  restricted: boolean;
  dimensions: UsageDimensionView[];
  pending: { waitingForCapacity: number; approvedInFlight: number };
}


const LABELS: Readonly<Record<UsageDimensionView['key'], { title: string; noun: string }>> = {
  crm_changes_daily: { title: 'CRM changes today', noun: 'changes' },
  email_messages_daily: { title: 'Email processing today', noun: 'emails' },
  card_captures_daily: { title: 'Business cards today', noun: 'cards' },
  csv_rows_monthly: { title: 'CSV imports this month', noun: 'rows' },
  csv_rows_per_file: { title: 'Rows per CSV file', noun: 'rows' },
  ai_cost_cents_daily: { title: 'AI processing today', noun: '' },
  ai_cost_cents_monthly: { title: 'AI processing this month', noun: '' },
};

const n = (v: number) => v.toLocaleString('en-US');
const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const isMoney = (k: UsageDimensionView['key']) => k === 'ai_cost_cents_daily' || k === 'ai_cost_cents_monthly';

export interface UsageLine {
  readonly key: UsageDimensionView['key'];
  readonly title: string;
  /** The headline: "12 of 20 used", "240 processed", "Up to 10,000 rows". */
  readonly headline: string;
  /** "8 remaining", or null when there is no limit to remain against. */
  readonly remaining: string | null;
  /** 0–100 for the bar, or null when there is nothing to fill. */
  readonly percent: number | null;
  /** Why this number, when it is not simply the plan: a safety limit, a hold, an estimate. */
  readonly notes: readonly string[];
  /** The reset instant, for the viewer's own clock. Null for a per-file limit. */
  readonly resetsAt: string | null;
  readonly exhausted: boolean;
}

export function usageLine(d: UsageDimensionView): UsageLine {
  const { title, noun } = LABELS[d.key];
  const money = isMoney(d.key);
  const fmt = (v: number) => (money ? dollars(v) : n(v));
  const notes: string[] = [];
  // A limit counts only if something applies it; otherwise the line shows the count alone.
  const limit = d.enforced ? d.limit : null;

  // Every AI call runs on the customer's own key, so the bill is Anthropic's, not ours.
  if (money) notes.push('An estimate from what the AI read. Anthropic bills your own account; this is not a charge from us.');

  if (d.period === 'file') {
    return {
      key: d.key, title,
      headline: limit === null ? 'No limit per file' : `Up to ${n(limit)} rows in one file`,
      remaining: null, percent: null, notes, resetsAt: null, exhausted: false,
    };
  }

  const used = d.used ?? 0;
  const counted = used + d.reserved;
  if (d.reserved > 0) {
    notes.push(`${n(d.reserved)} waiting for Keap to confirm. ${d.reserved === 1 ? 'It holds its place' : 'They hold their place'} until it does.`);
  }

  if (d.source === 'crm_not_enabled') {
    notes.push('Sending to Keap is not switched on for this workspace.');
  } else if (d.source === 'paused') {
    notes.push('Paused for this workspace. Contact support if this is unexpected.');
  } else if (d.source === 'restriction' && limit !== null) {
    notes.push(`Temporarily limited to ${fmt(limit)}.${d.allowance === null ? '' : ` Your plan allows ${fmt(d.allowance)}.`}`);
  } else if (d.source === 'safety_cap' && limit !== null) {
    notes.push(d.allowance === null || d.allowance === limit
      ? 'This is the daily safety limit for Keap changes.'
      : `This is the daily safety limit for Keap changes. Your plan allows ${fmt(d.allowance)}.`);
  }

  if (limit === null) {
    return {
      key: d.key, title,
      headline: money ? `${fmt(counted)} estimated` : `${fmt(counted)} ${noun} so far`,
      remaining: null, percent: null, notes, resetsAt: d.resetsAt, exhausted: false,
    };
  }

  const left = Math.max(0, limit - counted);
  return {
    key: d.key, title,
    headline: money ? `${fmt(counted)} of ${fmt(limit)} estimated` : `${fmt(counted)} of ${fmt(limit)} used`,
    remaining: `${fmt(left)} remaining`,
    percent: limit === 0 ? 100 : Math.min(100, Math.round((counted / limit) * 100)),
    notes, resetsAt: d.resetsAt, exhausted: left === 0,
  };
}

const ORDER: readonly UsageDimensionView['key'][] = [
  'crm_changes_daily', 'email_messages_daily', 'card_captures_daily',
  'csv_rows_monthly', 'csv_rows_per_file', 'ai_cost_cents_daily', 'ai_cost_cents_monthly',
];

export function usageLines(view: UsageView): UsageLine[] {
  const by = new Map(view.dimensions.map((d) => [d.key, d]));
  return ORDER.flatMap((k) => (by.has(k) ? [usageLine(by.get(k)!)] : []));
}

/** One section of the page: lines that share a unit, so rows, cards, dollars and CRM changes are never read as one thing. */
export interface UsageGroup {
  readonly key: 'crm' | 'capture' | 'file' | 'ai';
  readonly title: string;
  readonly hint: string;
  readonly lines: readonly UsageLine[];
}

const GROUPS: readonly { key: UsageGroup['key']; title: string; hint: string; keys: readonly UsageDimensionView['key'][] }[] = [
  {
    key: 'crm', title: 'Changes sent to Keap',
    hint: 'Contacts you approve, created or updated in Keap. The daily limit protects your Keap account from mistakes made in bulk.',
    keys: ['crm_changes_daily'],
  },
  {
    key: 'capture', title: 'What can come in',
    hint: 'Business cards, emails and imported CSV rows, each counted separately. None of these sends anything to Keap.',
    keys: ['card_captures_daily', 'email_messages_daily', 'csv_rows_monthly'],
  },
  {
    key: 'file', title: 'Size of one CSV file',
    hint: 'Applies to each file on its own. The rows of a file you import still count toward this month’s CSV imports.',
    keys: ['csv_rows_per_file'],
  },
  {
    key: 'ai', title: 'AI processing',
    hint: 'The AI that reads business cards and email, using your own Anthropic key.',
    keys: ['ai_cost_cents_daily', 'ai_cost_cents_monthly'],
  },
];

/** The page's sections, in order, each holding only the lines the server sent. */
export function usageGroups(view: UsageView): UsageGroup[] {
  const by = new Map(view.dimensions.map((d) => [d.key, d]));
  return GROUPS.flatMap((g) => {
    const lines = g.keys.flatMap((k) => (by.has(k) ? [usageLine(by.get(k)!)] : []));
    return lines.length === 0 ? [] : [{ key: g.key, title: g.title, hint: g.hint, lines }];
  });
}

/** "Resets at 8:00 PM", in the viewer's time zone, with the date when it is not today. */
export function resetText(iso: string, now: Date = new Date(), timeZone?: string): string {
  const at = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) };
  const day = (d: Date) => d.toLocaleDateString('en-US', timeZone ? { timeZone } : {});
  const time = at.toLocaleTimeString('en-US', opts);
  return day(at) === day(now)
    ? `Resets at ${time}`
    : `Resets ${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) })} at ${time}`;
}

/** The line under a card's "Save to Keap", or null when there is nothing to say. */
export function approvalUsageText(u: ApprovalUsage | undefined): { text: string; tone: 'info' | 'error' } | null {
  if (!u) return null;
  const needs = `This needs ${n(u.crmChangesRequired)} CRM change${u.crmChangesRequired === 1 ? '' : 's'}.`;
  const left = `You have ${n(u.crmChangesRemaining)} of today's ${n(u.crmDailyLimit)} remaining.`;
  if (u.fitsToday) return { text: `${needs} ${left}`, tone: 'info' };
  return {
    text: `${needs} ${left} Saving it now would be refused and nothing would be sent. It can be saved after the daily limit resets.`,
    tone: 'error',
  };
}
